import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Dialog, Transition } from '@headlessui/react';
import {
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  CheckIcon,
  ChevronUpDownIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { cn } from '../../shared/utils/cn';
import * as API from '../../api';

/**
 * Filters for the /events listing page.
 *
 * Controlled by the parent via `value` (a flat object) and `onApply`.
 *   value = { types: string[], city: string, minPrice: string, maxPrice: string }
 *     types — any of "Event" | "Activitie" | "Sejour" (empty = all)
 *     city  — city NAME (the events API matches on name, not id)
 *
 * Renders inline on desktop (sticky sidebar) and inside a bottom-sheet
 * Dialog on mobile. The mobile sheet keeps a local draft and only calls
 * onApply when the user taps "Show results".
 */

const TYPES = [
  { key: 'Event', labelKey: 'events.typeEvent' },
  { key: 'Activitie', labelKey: 'events.typeActivitie' },
  { key: 'Sejour', labelKey: 'events.typeSejour' },
];

function TypeChips({ types, onToggle }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      {TYPES.map(({ key, labelKey }) => {
        const active = types.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => onToggle(key)}
            aria-pressed={active}
            className={cn(
              'rounded-full border px-4 py-2 text-sm font-medium transition-all duration-200',
              active
                ? 'border-brand-500 bg-brand-500 text-white shadow-card'
                : 'border-slate-200 bg-white text-ink hover:border-brand-300 hover:text-brand-500'
            )}
          >
            {t(labelKey)}
          </button>
        );
      })}
    </div>
  );
}

function CityCombobox({ cities, city, onChange }) {
  const { t } = useTranslation();
  // The text shown in the input. Kept in local state (not via Headless UI's
  // `displayValue`) so a programmatic reset of `city` (e.g. the sheet's "Reset"
  // button) actually clears the field — `displayValue` only re-syncs on blur.
  const [query, setQuery] = useState(city || '');

  useEffect(() => {
    setQuery(city || '');
  }, [city]);

  const filtered = useMemo(() => {
    // When the field shows the selected city verbatim, list everything so the
    // dropdown isn't filtered down to a single match.
    if (!query || query === city) return cities;
    const q = query.toLowerCase();
    return cities.filter((c) => c.name.toLowerCase().includes(q));
  }, [cities, query, city]);

  const handleSelect = (val) => {
    onChange(val);
    setQuery(val || '');
  };

  return (
    <Combobox value={city} onChange={handleSelect} nullable>
      <div className="relative">
        <div className="relative">
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3">
            <MapPinIcon className="h-4 w-4 text-brand-500" />
          </span>
          <Combobox.Input
            className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-9 pr-9 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 rtl:pl-9 rtl:pr-9"
            placeholder={t('events.cityPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex items-center pr-2 rtl:right-auto rtl:left-0 rtl:pr-0 rtl:pl-2">
            <ChevronUpDownIcon className="h-4 w-4 text-ink-subtle" />
          </Combobox.Button>
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          afterLeave={() => setQuery(city || '')}
        >
          <Combobox.Options className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-xl border border-slate-100 bg-white py-1 text-sm shadow-elevated focus:outline-none">
            <Combobox.Option
              value=""
              className={({ active }) =>
                cn('relative cursor-pointer select-none px-4 py-2', active ? 'bg-brand-50 text-brand-700' : 'text-ink')
              }
            >
              {t('events.cityAll')}
            </Combobox.Option>
            {filtered.map((c) => (
              <Combobox.Option
                key={c.id}
                value={c.name}
                className={({ active }) =>
                  cn(
                    'relative cursor-pointer select-none py-2 pl-9 pr-4 rtl:pl-4 rtl:pr-9',
                    active ? 'bg-brand-50 text-brand-700' : 'text-ink'
                  )
                }
              >
                {({ selected }) => (
                  <>
                    <span className={cn('block truncate', selected && 'font-semibold')}>{c.name}</span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-500 rtl:left-auto rtl:right-0 rtl:pl-0 rtl:pr-3">
                        <CheckIcon className="h-4 w-4" />
                      </span>
                    ) : null}
                  </>
                )}
              </Combobox.Option>
            ))}
            {filtered.length === 0 ? (
              <div className="px-4 py-2 text-ink-subtle">—</div>
            ) : null}
          </Combobox.Options>
        </Transition>
      </div>
    </Combobox>
  );
}

function PriceRange({ minPrice, maxPrice, onChange }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        inputMode="numeric"
        min="0"
        value={minPrice}
        onChange={(e) => onChange('minPrice', e.target.value)}
        placeholder={t('events.priceMin')}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />
      <span className="text-ink-subtle">—</span>
      <input
        type="number"
        inputMode="numeric"
        min="0"
        value={maxPrice}
        onChange={(e) => onChange('maxPrice', e.target.value)}
        placeholder={t('events.priceMax')}
        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
      />
    </div>
  );
}

function FieldGroup({ label, children }) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-subtle">{label}</p>
      {children}
    </div>
  );
}

// The shared body of controls — reused inline (desktop) and in the sheet (mobile).
function FiltersBody({ draft, cities, setType, setField, setCity }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <FieldGroup label={t('events.type')}>
        <TypeChips types={draft.types} onToggle={setType} />
      </FieldGroup>
      <FieldGroup label={t('events.city')}>
        <CityCombobox cities={cities} city={draft.city} onChange={setCity} />
      </FieldGroup>
      <FieldGroup label={t('events.price')}>
        <PriceRange minPrice={draft.minPrice} maxPrice={draft.maxPrice} onChange={setField} />
      </FieldGroup>
    </div>
  );
}

export default function EventsFilters({ value, onApply, resultCount, loading }) {
  const { t } = useTranslation();
  const [cities, setCities] = useState([]);
  const [open, setOpen] = useState(false);
  // Desktop edits apply live; mobile uses a draft committed on "Show results".
  const [draft, setDraft] = useState(value);

  useEffect(() => {
    let mounted = true;
    API.AllCities()
      .then((res) => mounted && setCities(res?.data || []))
      .catch(() => mounted && setCities([]));
    return () => {
      mounted = false;
    };
  }, []);

  // Keep the draft in sync when the parent (URL) changes outside the sheet.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  const activeCount =
    value.types.length + (value.city ? 1 : 0) + (value.minPrice || value.maxPrice ? 1 : 0);

  const toggleType = (setter, current) => (key) => {
    const next = current.types.includes(key)
      ? current.types.filter((x) => x !== key)
      : [...current.types, key];
    setter({ ...current, types: next });
  };
  const setFieldOn = (setter, current) => (name, val) => setter({ ...current, [name]: val });
  const setCityOn = (setter, current) => (city) => setter({ ...current, city: city || '' });

  // ----- desktop: live apply -----
  const desktopToggle = toggleType(onApply, value);
  const desktopField = setFieldOn(onApply, value);
  const desktopCity = setCityOn(onApply, value);

  // ----- mobile sheet: draft -----
  const draftToggle = toggleType(setDraft, draft);
  const draftField = setFieldOn(setDraft, draft);
  const draftCity = setCityOn(setDraft, draft);

  const clearAll = () => onApply({ types: [], city: '', minPrice: '', maxPrice: '' });

  return (
    <>
      {/* ---------- Desktop sticky sidebar ---------- */}
      <aside className="hidden lg:block">
        <div className="sticky top-24 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
          <div className="mb-5 flex items-center justify-between">
            <h2 className="font-display text-lg font-semibold text-ink">{t('events.filtersTitle')}</h2>
            {activeCount > 0 ? (
              <button
                type="button"
                onClick={clearAll}
                className="text-xs font-semibold text-brand-500 hover:underline"
              >
                {t('events.clearAll')}
              </button>
            ) : null}
          </div>
          <FiltersBody
            draft={value}
            cities={cities}
            setType={desktopToggle}
            setField={desktopField}
            setCity={desktopCity}
          />
        </div>
      </aside>

      {/* ---------- Mobile trigger button (sticky) ---------- */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-ink shadow-card lg:hidden"
      >
        <AdjustmentsHorizontalIcon className="h-5 w-5 text-brand-500" />
        {t('events.filters')}
        {activeCount > 0 ? (
          <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-brand-500 px-1.5 text-[11px] font-bold text-white">
            {activeCount}
          </span>
        ) : null}
      </button>

      {/* ---------- Mobile bottom sheet ---------- */}
      <Transition show={open} as={Fragment}>
        <Dialog onClose={() => setOpen(false)} className="relative z-50 lg:hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-ink/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-x-0 bottom-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="translate-y-full"
              enterTo="translate-y-0"
              leave="ease-in duration-200"
              leaveFrom="translate-y-0"
              leaveTo="translate-y-full"
            >
              <Dialog.Panel className="flex max-h-[88vh] flex-col rounded-t-3xl bg-white shadow-elevated">
                <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
                  <Dialog.Title className="font-display text-lg font-semibold text-ink">
                    {t('events.filtersTitle')}
                  </Dialog.Title>
                  <button
                    type="button"
                    onClick={() => setOpen(false)}
                    aria-label="Close"
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-surface-muted text-ink-muted hover:text-ink"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-5">
                  <FiltersBody
                    draft={draft}
                    cities={cities}
                    setType={draftToggle}
                    setField={draftField}
                    setCity={draftCity}
                  />
                </div>

                <div className="flex items-center gap-3 border-t border-slate-100 px-5 py-4">
                  <button
                    type="button"
                    onClick={() => setDraft({ types: [], city: '', minPrice: '', maxPrice: '' })}
                    className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-ink hover:bg-surface-muted"
                  >
                    {t('events.clear')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onApply(draft);
                      setOpen(false);
                    }}
                    className="flex-1 rounded-xl bg-brand-500 px-4 py-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-600"
                  >
                    {loading
                      ? t('events.resultsLoading')
                      : resultCount != null
                      ? t('events.showResults')
                      : t('events.apply')}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
}
